import { useEffect, useState } from "react";
import { ConfigProvider, Modal, theme } from "antd";
import { useAuth } from "@/hooks/Auth";

/**
 * Aviso de encerramento por inatividade, com contagem regressiva.
 *
 * O prazo (`avisoInatividadeAte`) é um timestamp absoluto, controlado pelo
 * sessionManager — aqui só se desenha o tempo restante. Isso mantém a contagem
 * correta mesmo quando o navegador estrangula os timers da aba.
 *
 * Fica montado no PrivateLayout (e não como Modal.confirm estático) para
 * assinar o AuthContext naturalmente, existir apenas quando há sessão e ter o
 * próprio ConfigProvider escuro, seguindo a convenção de tema por página.
 */

const formatarRestante = (ms: number) => {
  const totalSegundos = Math.max(0, Math.ceil(ms / 1000));
  const minutos = Math.floor(totalSegundos / 60);
  const segundos = totalSegundos % 60;
  return `${minutos}:${String(segundos).padStart(2, "0")}`;
};

export const AvisoInatividadeModal = () => {
  const { avisoInatividadeAte, continuarSessao, signOut } = useAuth();
  const [restanteMs, setRestanteMs] = useState(0);

  useEffect(() => {
    if (!avisoInatividadeAte) return;

    const atualizar = () => setRestanteMs(avisoInatividadeAte - Date.now());
    atualizar();

    const id = setInterval(atualizar, 1000);
    return () => clearInterval(id);
  }, [avisoInatividadeAte]);

  return (
    <ConfigProvider
      theme={{ algorithm: [theme.darkAlgorithm, theme.compactAlgorithm] }}
    >
      <Modal
        open={Boolean(avisoInatividadeAte)}
        title="Continuar conectado?"
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText="Continuar conectado"
        cancelText="Sair agora"
        onOk={continuarSessao}
        onCancel={() => signOut("inatividade")}
      >
        <p>
          Sua sessão será encerrada por inatividade em{" "}
          <strong>{formatarRestante(restanteMs)}</strong>.
        </p>
        <p>Clique em “Continuar conectado” para seguir usando o sistema.</p>
      </Modal>
    </ConfigProvider>
  );
};

export default AvisoInatividadeModal;
